type TimeDataList = TimeDataMap[];

interface TimeDataMap {
  start: number;
  end: number;
}

interface LectureTimeStampMap {
  [key: string]: TimeDataList;
}

interface LectureMetaDataMap {
  [key: string]: {
    time_data_list: TimeDataList;
    is_header?: boolean;
    chapter_id?: string;
    subject_id?: string;
  };
}

interface TransformedLectureTimeStamp {
  [key: string]: number;
}

interface LecturesWatchedItem {
  id: string;
  time_spent: number;
  is_header?: boolean;
  chapter_id?: string;
  subject_id?: string;
}

interface LecturesWatchedMap {
  [key: string]: LecturesWatchedItem;
}

interface LectureMap {
  lectures_watched: LecturesWatchedMap;
  total_time_spent: number;
}

export class Transform {
  private static transformTimeData(arr: TimeDataList): number {
    let _arr = [...arr];
    return _arr.reduce((acc, cur) => {
      if (cur.end > cur.start) acc += cur.end - cur.start;
      return acc;
    }, 0);
  }

  private static transformLectureTimeStamp(
    dataMap: LectureTimeStampMap
  ): TransformedLectureTimeStamp {
    let _transformDataMap = {};

    for (let key in dataMap) {
      _transformDataMap[key] = Transform.transformTimeData(dataMap[key]);
    }

    return _transformDataMap;
  }

  static shapeLectureMap(dataMap: LectureTimeStampMap): LectureMap {
    const transformedLectureTimeStamp =
      Transform.transformLectureTimeStamp(dataMap);

    let _shapedLectureMap = {
      lectures_watched: {},
      total_time_spent: 0,
    };
    for (let key in transformedLectureTimeStamp) {
      _shapedLectureMap.lectures_watched[key] = {
        id: key,
        time_spent: Math.round(transformedLectureTimeStamp[key]),
      };
      _shapedLectureMap.total_time_spent += transformedLectureTimeStamp[key];
    }

    _shapedLectureMap.total_time_spent = Math.round(
      _shapedLectureMap.total_time_spent
    );

    return _shapedLectureMap;
  }

  private static transformLectureTimeStampWithMetaData(
    dataMap: LectureMetaDataMap
  ): TransformedLectureTimeStamp {
    let _transformDataMap = {};

    for (let key in dataMap) {
      _transformDataMap[key] = Transform.transformTimeData(
        dataMap[key].time_data_list
      );
    }

    return _transformDataMap;
  }

  static shapeLectureMapWithMetaData(dataMap: LectureMetaDataMap): LectureMap {
    const transformedLectureTimeStamp =
      Transform.transformLectureTimeStampWithMetaData(dataMap);

    let _shapedLectureMap = {
      lectures_watched: {},
      total_time_spent: 0,
    };
    for (let key in transformedLectureTimeStamp) {
      _shapedLectureMap.lectures_watched[key] = {
        id: key,
        chapter_id: dataMap[key].chapter_id,
        subject_id: dataMap[key].subject_id,
        is_header: dataMap[key].is_header,
        time_spent: Math.round(transformedLectureTimeStamp[key]),
      };
      _shapedLectureMap.total_time_spent += transformedLectureTimeStamp[key];
    }

    _shapedLectureMap.total_time_spent = Math.round(
      _shapedLectureMap.total_time_spent
    );

    return _shapedLectureMap;
  }
}

export function delay(timeout = 5000) {
  let timer;

  return (fn) => {
    if (timer) return;
    timer = setTimeout(() => {
      fn();
      timer = null;
    }, timeout);
  };
}

export function omitUndefinedKeys(obj: any) {
  let _obj = { ...obj };
  for (let key in _obj) {
    if (_obj[key] === undefined) delete _obj[key];
  }
  return _obj;
}

export function isDestructible(obj): boolean {
  if (!obj) return false;
  if (typeof obj === "object") {
    return true;
  }
  return Array.isArray(obj) && obj.length > 0;
}

export function safeDestructible(obj: any): Record<any, any> {
  if (!obj) return {};
  if (typeof obj === "object") {
    return obj;
  }
  if (Array.isArray(obj) && obj.length > 0) {
    return obj;
  }
  return {};
}

/**
 * Fetches documents from the 'days' subcollection for the current and previous two months.
 * Assumes a Firestore structure like: /user_engagement/interaction_engagement/lecture_engagement/{YYYY_M}/days
 * Uses the older Firestore SDK syntax (e.g., firebase.firestore() or admin.firestore()).
 *
 * @param {firebase.firestore.Firestore | firebase_admin.firestore.Firestore} db - The initialized Firestore database instance.
 * @returns {Promise<Array<object>>} A promise that resolves to an array containing all documents
 * (with their IDs) from the 'days' subcollections of the
 * existing target months. Returns an empty array if none of
 * the target months exist or have documents.
 */
export async function getLatestThreeMonthsEngagementData(db) {
  const basePath = "/user_engagement/interaction_engagement/lecture_engagement";
  const allFetchedDocs = [];

  // Use the current date to determine the months to fetch
  // Based on context, current date is ~April 15, 2025
  const today = new Date(2025, 3, 15); // Note: JavaScript months are 0-indexed (0=Jan, 3=Apr)

  const promises = []; // To hold the promises for fetching data concurrently

  console.log(`Current date used for calculation: ${today.toDateString()}`);

  for (let i = 0; i < 3; i++) {
    // Calculate the target date by subtracting 'i' months (0, 1, 2)
    // Create a new date object based on 'today' for each iteration
    const targetDate = new Date(today);
    // Set day to 1 first to avoid issues when month lengths differ (e.g., going from March 31st back one month)
    targetDate.setDate(1);
    targetDate.setMonth(today.getMonth() - i);

    const year = targetDate.getFullYear();
    const month = targetDate.getMonth() + 1; // Get 1-indexed month (1=Jan, 12=Dec)
    const monthDocId = `${year}_${month}`; // Format: YYYY_M

    const collectionPath = `${basePath}/${monthDocId}/days`;

    console.log(`Attempting to fetch documents from: ${collectionPath}`);

    // Create a promise for fetching documents from this specific month's 'days' collection
    const fetchPromise = db
      .collection(collectionPath)
      .get()
      .then((snapshot) => {
        const monthDocs = [];
        if (!snapshot.empty) {
          snapshot.forEach((doc) => {
            // Store document data along with its ID, which is often useful
            monthDocs.push({ id: doc.id, ...doc.data() });
          });
          console.log(
            `Successfully fetched ${monthDocs.length} documents from ${collectionPath}`
          );
        } else {
          console.log(
            `No documents found in ${collectionPath} (collection might be empty or not exist).`
          );
        }
        return monthDocs; // Return the documents fetched for this month
      })
      .catch((error) => {
        // Log errors, but return an empty array so Promise.all doesn't fail
        // Firestore's get() typically doesn't throw an error for non-existent collections,
        // it just returns an empty snapshot. This catch is for other potential errors.
        console.error(
          `Error fetching documents from ${collectionPath}:`,
          error
        );
        return []; // Return empty array on error for this specific month
      });

    promises.push(fetchPromise);
  }

  // Wait for all the fetch operations to complete
  try {
    const results = await Promise.all(promises);
    // results is an array of arrays (one array of documents per month)
    // Flatten the array of arrays into a single array of documents
    const finalDocs = results.flat();
    console.log(
      `Total documents fetched across all months: ${finalDocs.length}`
    );
    return finalDocs;
  } catch (error) {
    // This catch is unlikely to be hit due to individual catches, but good practice
    console.error("Error executing Promise.all for fetching documents:", error);
    return []; // Return empty array in case of unexpected Promise.all failure
  }
}

// --- Example Usage ---
/*
// Assuming you have initialized Firestore, either via Firebase Admin SDK (Node.js)
// const admin = require('firebase-admin');
// admin.initializeApp({ ... });
// const db = admin.firestore();

// Or via Firebase Web SDK (Client-side)
// const firebase = require('firebase/app'); // Or import firebase from 'firebase/app'
// require('firebase/firestore'); // Or import 'firebase/firestore'
// firebase.initializeApp({ ... });
// const db = firebase.firestore();


// Call the function
getLatestThreeMonthsEngagementData(db)
  .then(documents => {
    if (documents.length > 0) {
      console.log("Fetched documents:", JSON.stringify(documents, null, 2));
      // Process the documents array here
    } else {
      console.log("No documents found for the last three months.");
    }
  })
  .catch(error => {
    console.error("An error occurred:", error);
  });

*/

// export async function fetchAndLogChunks() {
//   const apiUrl = "/api/gemini"; // Change to your actual API endpoint

//   try {
//     console.log(`Workspaceing stream from ${apiUrl}...`);
//     const response = await fetch(apiUrl, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         "Transfer-Encoding": "chunked",
//       },
//       body: JSON.stringify({
//         query: "How to learn integers on Pustack?",
//         syllabus: "NCERT",
//       }),
//     });

//     if (!response.ok) {
//       throw new Error(`HTTP error! Status: ${response.status}`);
//     }

//     if (!response.body) {
//       throw new Error("Response has no body.");
//     }

//     const reader = response.body.getReader();
//     const decoder = new TextDecoder(); // To convert bytes (Uint8Array) to string
//     let buffer = ""; // To accumulate text chunks

//     console.log("Starting to read and log stream...");

//     // Loop reading chunks from the stream
//     while (true) {
//       const { done, value } = await reader.read();

//       if (done) {
//         console.log("Stream finished.");
//         // Process any leftover data in the buffer when the stream ends
//         if (buffer.trim()) {
//           console.log("Processing final buffer content:", buffer);
//           tryParseAndLog(buffer);
//         }
//         break; // Exit the loop
//       }

//       // Decode the chunk (Uint8Array) to string and add to buffer
//       const textChunk = decoder.decode(value, { stream: true });
//       buffer += textChunk;
//       // Don't log the raw text chunk here, wait for complete JSON

//       // Try to process complete JSON objects separated by newline from the buffer
//       let newlineIndex;
//       while ((newlineIndex = buffer.indexOf("\n")) >= 0) {
//         // Extract the part up to the newline
//         const jsonString = buffer.substring(0, newlineIndex).trim();
//         // Remove the extracted part (and  newline) from the buffer
//         buffer = buffer.substring(newlineIndex + 1);

//         // Parse and log if it's not empty
//         if (jsonString) {
//           tryParseAndLog(jsonString);
//         }
//       }
//       // Any incomplete JSON string remains in 'buffer' for the next read()
//     }
//   } catch (error) {
//     console.error("Failed to fetch or process stream:", error);
//   }
// }

// // Helper function to safely parse and log the JSON string
// function tryParseAndLog(jsonString) {
//   try {
//     const jsonData = JSON.parse(jsonString);
//     console.log("✅ Received and parsed JSON object:", jsonData); // <-- LOGGING HERE
//   } catch (e) {
//     console.error("❌ Failed to parse JSON string:", e);
//     console.error("   Received string was:", jsonString);
//   }
// }
